import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { MarkdownField } from 'components/collab/lab/LabDialogs';
import { EMPLOYMENT, HOST_LABEL, KIND, WORK_MODE } from './positionLabels';

const BLANK = { title: '', kind: 'phd', employment_type: 'full_time', work_mode: 'onsite', location: '', compensation: '',
  description: '', deadline: '', apply_url: '', contact_email: '', is_open: true };

/** Announce a job opening (or edit one). The host is a lab you edit, a consortium you lead (not a publication
 *  consortium), or an organization you have a verified affiliation with. */
export default function PositionDialog({ open, onClose, position, onSaved }) {
  const editing = Boolean(position);
  const { data: hosts } = useSWR(open && !editing ? '/consortium/positions/hosts/' : null, fetcher);
  const [form, setForm] = useState(BLANK);
  const [host, setHost] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm(position ? Object.fromEntries(Object.keys(BLANK).map((k) => [k, position[k] ?? BLANK[k]])) : BLANK);
  }, [open, position]);
  useEffect(() => { if (hosts?.length && !host) setHost(`${hosts[0].type}:${hosts[0].id}`); }, [hosts, host]);

  const save = async () => {
    setBusy(true); setError(null);
    const body = { ...form, deadline: form.deadline || null };
    try {
      let res;
      if (editing) {
        res = await axiosService.patch(`/consortium/positions/${position.id}/`, body);
      } else {
        const [host_type, host_id] = host.split(':');
        res = await axiosService.post('/consortium/positions/', { ...body, host_type, host_id });
      }
      onSaved(res.data);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the position.'));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editing ? 'Edit position' : 'Announce a position'}</DialogTitle>
      <DialogContent>
        {!editing && hosts && hosts.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            Anyone with a verified affiliation can announce positions for their organization, for example a professor
            for their university or a CEO for their company. Labs (their PI and editors) and research consortia (their
            coordinator or project leader) can too. Add your affiliation on your profile and have it verified to post one.
          </Typography>
        ) : (
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {!editing && (
              <TextField select label="Announced by" value={host} onChange={(e) => setHost(e.target.value)}>
                {(hosts || []).map((h) => (
                  <MenuItem key={`${h.type}:${h.id}`} value={`${h.type}:${h.id}`}>
                    {h.name} <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{HOST_LABEL[h.type]}{h.type !== 'organization' && h.organization ? ` · ${h.organization}` : ''}</Typography>
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField label="Position title" required value={form.title} onChange={set('title')} placeholder="e.g. Bioinformatics engineer" />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
              <TextField select label="Type" value={form.kind} onChange={set('kind')}>
                {Object.entries(KIND).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
              </TextField>
              <TextField select label="Employment" value={form.employment_type} onChange={set('employment_type')}>
                {Object.entries(EMPLOYMENT).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
              </TextField>
              <TextField select label="Where" value={form.work_mode} onChange={set('work_mode')}>
                {Object.entries(WORK_MODE).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
              </TextField>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 2 }}>
              <TextField label="Location" value={form.location} onChange={set('location')} placeholder="City, country" />
              <TextField label="Apply by" type="date" value={form.deadline || ''} onChange={set('deadline')} slotProps={{ inputLabel: { shrink: true } }} />
            </Box>
            <MarkdownField label="Description: the role, requirements, what you offer" value={form.description}
              onChange={(description) => setForm({ ...form, description })} />
            <TextField label="Salary / stipend (optional)" value={form.compensation} onChange={set('compensation')} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label="External application link (optional)" value={form.apply_url} onChange={set('apply_url')} placeholder="https://" />
              <TextField label="Contact email (optional)" value={form.contact_email} onChange={set('contact_email')} />
            </Box>
            <Typography variant="caption" color="text.secondary">
              People apply on Otable with a cover note and their CV; you see the applications on the position&apos;s page.
            </Typography>
            {editing && (
              <FormControlLabel label="Open for applications" control={<Switch checked={Boolean(form.is_open)}
                onChange={(e) => setForm({ ...form, is_open: e.target.checked })} />} />
            )}
          </Stack>
        )}
        {error && <Typography color="error" variant="body2" sx={{ mt: 2 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {(editing || hosts?.length > 0) && (
          <Button variant="contained" onClick={save} disabled={busy || !form.title.trim() || (!editing && !host)}>
            {editing ? 'Save' : 'Announce'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
